/**
 * App Entry File
 * Copyright 2018. All Rights Reserved
 * Created By: Teaming, LLC
 */
import "@/helpers/progress";
import "@fortawesome/fontawesome-free/css/all.css";
import Vue from "vue";
import Vuetify from "vuetify";
import { VueExtendLayout, layout } from "vue-extend-layout";
import VueBreadcrumbs from "vue2-breadcrumbs";
import VueNotifications from "vue-notifications";
import miniToastr from "mini-toastr";
import VModal from "vue-js-modal";
import Nprogress from "nprogress";
import AppConfig from "@/constants/AppConfig";
import VueMoment from "vue-moment";
import VueTour from "vue-tour";
import VueHotkey from "v-hotkey";
import fullscreen from "vue-fullscreen";
import VueClipboard from "vue-clipboard2";
import VueLetterAvatar from "vue-letter-avatar";
import PortalVue from "portal-vue";
import Rollbar from "vue-rollbar";
import "@/filters";
import $ from "jquery";
import { i18n } from "./i18n";
// global components
import GlobalComponents from "./globalComponents";
import authPluginFactory from "@/helpers/authPlugin";
import { getAuthServiceInstance } from "@/helpers/authService";
import "@/helpers/scrollMachine";
import { machine as socketMachine } from "@/api/socketAPI";

// router
import router from "./router";

// store
import { store } from "./store/store";

// include script file
import "./lib/MeetingfulScript";

// include all css files
import "./lib/MeetingfulCss";

import intercomFactory from "./lib/intercom";
import fullstoryTelemetryFactory from "@/lib/fullstory";

const intercom = intercomFactory( { store, } );
const fullStory = fullstoryTelemetryFactory( { store, } );

// session
const auth = getAuthServiceInstance();
const AuthPlugin = authPluginFactory( auth );

auth.on( "loginEvent", authResult => {
	if ( !authResult.loggedIn ) {
		intercom.shutdown();
	}

	if ( !authResult.loggedIn && authResult.error ) {
		const { error, } = authResult;
		console.error( error );
		const message = error.message || i18n.t( "auth.unknownErrorMessage" );
		VueNotifications.error( { message, } );
		router.push( { name: authResult.redirectTo || "login", } );
	}

	if ( !authResult.isEmailVerified ) {
		return window.location.assign( "/t2/logout" );
	}

	store.commit( "auth/onSessionUpdate", authResult );
	store.commit( "userProfile/userProfileLoaded", authResult?.user );
	store.dispatch( "schedule/refreshPermissions" );

	setImmediate( () => {
		intercom.update();
		fullStory.identify();
		store.dispatch( "userProfile/reloadUserProfile" );
	} );
} );

auth.on( "calPermsRequestInitiated", () => {
	store.dispatch( "userProfile/indicateCalendarPermsRequestInProgress" );
} );

// we need to ensure the user profile is reloaded if the user
// has just authorized calendar integration
auth.on( "calPermsRequestCompleted", () => {
	router.push( { name: "schedule", query: { sync: "complete", }, } );
} );

auth.on( "calPermsRequestError", ( { email, targetEmail, } ) => {
	store.dispatch( "userProfile/indicateCalendarPermsRequestError", { email, targetEmail, } );
} );

window.addEventListener( "storage", async event => {
	if ( event.key !== "discAssessmentResults" ) {
		return;
	}
	try {
		const assessment = JSON.parse( window.localStorage.getItem( "discAssessmentResults" ) );
		if ( assessment ) {
			await store.dispatch( "userProfile/saveDiscAssessmentResponse", assessment );
			if ( router.history.current.name !== "profile" ) {
				return;
			}
			router.replace( { path: "/me?postassessment=1", } );
		}
	} catch ( error ) {
		console.error( "error parsing DISC assessment results", error );
	}
} );

window.addEventListener( "resize", () => {
	// close the mobile sidebar if the window is resized
	store.dispatch( "sidebar/closeNavSidebar" );
} );

// navigation guards before each
router.beforeEach( async ( to, from, next ) => {
	// HACK: it is easiest to force this redirect here, instead of
	// accounting for it in several components.
	const goNext = options => {
		const user = ( store.getters[ "userProfile/userProfile" ] || {} );
		if ( user?.personId && !user.isLegacyMeetingUser ) {
			if ( to.name.startsWith( "schedule" ) ) {
				return window.location.assign( "/t2/one-on-ones" );
			}
		}

		next( options );
	};

	//
	// authentication checks
	//
	Nprogress.start();

	let isAuthenticated = auth.isAuthenticated();
	if ( !isAuthenticated && auth.shouldAttemptRenewOnPath( to.path ) ) {
		try {
			await auth.renewTokens();
			isAuthenticated = auth.isAuthenticated();
		} catch ( e ) {
			// no-op
			isAuthenticated = false;
		}
	}

	if ( to.matched.some( ( { meta: { requiresAuth, }, } ) => requiresAuth ) && !isAuthenticated ) {
		// this route requires auth, try to renew tokens
		// if we can't, redirect to login page.
		try {
			await auth.renewTokens();
			if ( auth.isAuthenticated() ) {
				return goNext();
			}
		} catch ( e ) {
			// no-op
		} finally {
			Nprogress.done();
		}

		return goNext( {
			path: "/session/login",
			query: { redirect: to.fullPath, },
		} );
	}

	Nprogress.done();

	goNext(); // make sure to always call goNext()!
} );

// navigation guard after each
router.afterEach( () => {
	Nprogress.done();

	setTimeout( () => {
		$( ".v-content__wrap" ).scrollTop( 0, 0 );
		$( ".app-boxed-layout .app-content" ).scrollTop( 0, 0 );
		$( ".app-mini-layout .app-content" ).scrollTop( 0, 0 );
	}, 200 ); // eslint-disable-line no-magic-numbers

	setTimeout( () => {
		store.dispatch( "trialEndDialog/tryShowTrialEndDialog" );
		// eslint-disable-next-line no-magic-numbers
	}, 1000 );
} );

function toast( { title, message, type, timeout, cb, } ) {
	return miniToastr[ type ]( message, title, timeout, cb );
}

const options = {
	success: toast,
	error: toast,
	info: toast,
	warn: toast,
};

const toastTypes = {
	success: "success",
	error: "error",
	info: "info",
	warn: "warn",
};

miniToastr.init( { types: toastTypes, allowHtml: true, } );

// plugins
Vue.use( AuthPlugin );
Vue.use( Vuetify );
Vue.use( VModal );
Vue.use( VueTour );
Vue.use( VueHotkey );
Vue.use( VueBreadcrumbs );
Vue.use( VueExtendLayout );
Vue.use( VueNotifications, options );
Vue.use( fullscreen );
Vue.use( PortalVue );
Vue.use( GlobalComponents );
Vue.use( VueLetterAvatar );
Vue.use( VueMoment );

Vue.use( Rollbar, {
	accessToken: AppConfig.rollbarToken,
	captureUncaught: true,
	captureUnhandledRejections: true,
	enabled: process.env.NODE_ENV !== "development",
	source_map_enabled: process.env.NODE_ENV !== "development",
	environment: AppConfig.rollbarEnv.replace( /^rollbar_/, "" ),
	guess_uncaught_frames: true,
	payload: {
		client: {
			javascript: {
				code_version: AppConfig.rollbarEnv, // eventually this should be the git sha as part of build
			},
		},
	},
} );

Vue.config.errorHandler = function( err, vm, info ) {
	console.error( err, vm, info );
	Vue.rollbar.error( err ); // log to rollbar
	console.trace();
};

VueClipboard.config.autoSetContainer = true;
Vue.use( VueClipboard );

const vuetify = new Vuetify( {
	theme: store.getters.selectedTheme.theme,
} );

Vue.prototype.$console = console;
Vue.prototype.$intercom = intercom;

/* eslint-disable no-new */
new Vue( {
	store,
	el: "#app",
	i18n,
	router,
	vuetify,
	...layout,
} );

store.watch(
	state => {
		return {
			editingOutcomeId: state.meetingAgenda.editingOutcomeId,
		};
	},
	newVal => {
		const editingOutcomeId = !!newVal.editingOutcomeId;
		socketMachine.handle( editingOutcomeId ? "pause" : "resume" );
	}
);

// @DELETEME - we long to hold the sky, but we never will...
window.__TEAMINGSTORE__ = store;



// WEBPACK FOOTER //
// ./src/main.js